import { MouseEvent, useState } from 'react';

import {
  Box,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';

import { IcArrowDown, ICRapidSOSWithoutPin } from '@/assets/images';
import { FormCard } from '@/components/Cards';
import CallDetailsModal from '@/components/Modals/CallDetailsModal/CallDetailsModal';
import { CallCard } from '@/layouts/MainLayout/components/IncomingCall/components/CallCard';
import { IncomingCallMenuRoot } from '@/layouts/MainLayout/components/IncomingCall/styles';
import { ICall } from '@/models';
import { CFSReporterTimeItem } from '@/pages/CFS/Edit/components/CFSReporterTimeItem';
import { formatPhoneNumber } from '@/utils';

interface CallerInformationProps {
  reportTime?: any;
  handleChangeReporterTime?: (value: string) => Promise<void>;
  showRapidSOSIcon?: boolean;
  callerInformation?: string | undefined;
  organizationName?: string | undefined;
  calls?: ICall[];
  readOnly?: boolean;
}

export const CallerInformation = ({
  reportTime,
  handleChangeReporterTime,
  callerInformation,
  showRapidSOSIcon,
  organizationName,
  calls,
  readOnly,
}: CallerInformationProps) => {
  const [menuState, setMenuState] = useState<{ anchorEl: null | HTMLElement }>({
    anchorEl: null,
  });

  const [selectedCall, setSelectedCall] = useState<ICall | null>(null);
  const [callDetailsModalOpen, setCallDetailsModalOpen] =
    useState<boolean>(false);
  const openActionMenu = Boolean(menuState.anchorEl);

  const handleOpenMenu = (e: MouseEvent<HTMLButtonElement>) => {
    setMenuState({ anchorEl: e.currentTarget });
  };

  const handleCloseMenu = () => {
    setMenuState({ anchorEl: null });
  };

  const onOpenInfoModal = (call: ICall) => {
    setSelectedCall(call);
    setCallDetailsModalOpen(call ? true : true);
  };

  return (
    <FormCard>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 500,
            marginLeft: '1rem',
            color: 'text.primary',
          }}
        >
          Caller ID Information
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: 'auto',
          }}
        >
          {callerInformation && (
            <Box
              sx={{
                margin: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {showRapidSOSIcon && <ICRapidSOSWithoutPin />}
              {organizationName ? (
                <Tooltip title={organizationName}>
                  <Box sx={{ color: 'text.primary' }}>
                    {formatPhoneNumber(callerInformation)}
                  </Box>
                </Tooltip>
              ) : (
                <Box sx={{ color: 'text.primary' }}>
                  {formatPhoneNumber(callerInformation)}
                </Box>
              )}
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            </Box>
          )}
          <CFSReporterTimeItem
            reporterTime={reportTime}
            onAccept={readOnly ? undefined : handleChangeReporterTime}
          />
          {calls && calls.length > 0 && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Chip label={calls.length}></Chip>
              <IconButton
                sx={{ aspectRatio: '1/1' }}
                onClick={(e) => handleOpenMenu(e)}
                aria-controls={openActionMenu ? 'action-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openActionMenu ? 'true' : undefined}
              >
                <IcArrowDown />
              </IconButton>
            </>
          )}
        </Box>
        {calls && calls.length > 0 && (
          <IncomingCallMenuRoot
            id="action-menu"
            anchorEl={menuState.anchorEl}
            open={openActionMenu}
            onClose={handleCloseMenu}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {calls.map((call) => (
              <MenuItem
                key={call._id}
                sx={{ padding: 0, mb: 1 }}
                onClick={() => {}}
              >
                <CallCard
                  {...call}
                  onInfoIconClick={() => onOpenInfoModal(call)}
                />
              </MenuItem>
            ))}
          </IncomingCallMenuRoot>
        )}
        {selectedCall && (
          <CallDetailsModal
            open={callDetailsModalOpen}
            call={selectedCall}
            onClose={() => {
              setCallDetailsModalOpen(false);
            }}
          />
        )}
      </Box>
    </FormCard>
  );
};
